import { Button, Card, Container, IconButton, MenuItem, OutlinedInput, Select, Stack, Table, TableBody, TableContainer, TableHead, TableRow } from "@mui/material";
import { memo, useCallback, useEffect, useState } from "react";
import { enqueueSnackbar } from "notistack";
import axios, { endpoints, formatEndpoint } from 'src/utils/axios';
import { useSettingsContext } from "src/components/settings";
import { EldDriver, EldDvir } from "src/models";
import useModelData, { deleteById, replaceById } from "src/hooks/use-model-data";
import Iconify from "src/components/iconify/iconify";
import SimpleDialog from "src/components/simple-dialog";
import { DisplayPageData } from "src/components/display-page-data";
import useModelSavingData from "src/hooks/use-model-saving-data";
import DisplayErrors from "src/components/display-errors";
import TruxTextField from "src/components/inputs/trux-text-field";
import TruxFormField from "src/components/inputs/trux-form-field";
import PageHeader from "src/components/page-header";
import { addDaysToDate, formatDatetimeForPreview, today } from "src/utils/date-utils";
import { useSort } from "src/hooks/use-sort";
import { SortableHeader } from "src/components/sortable-header";
import PageFilterLoader, { usePageFilterLoaderData } from "src/components/page-filter-loader";
import NoneFoundReport from "src/components/none-found-report";
import TruxAutocompleteField from "src/components/inputs/trux-autocomplete-field";
import { TruxTableCell } from "src/components/trux-table-cell";
import { useAuthContext } from "src/auth/hooks";
import Label from "src/components/label";
import TruxDateTimeField from "src/components/inputs/trux-date-time-field";
import { Colors } from "src/theme/palette";
import dayjs from "dayjs";


type EldDvirsProps = {
  eldDvirs?: EldDvir[],
  onEldDvirSelected: (a: EldDvir) => void,
  onEldDvirDeleteSelected: (a: EldDvir) => void,
  timezone: string,
}

const EldDvirs = memo(({ eldDvirs, onEldDvirSelected, onEldDvirDeleteSelected, timezone }: EldDvirsProps) => {
  const { sortedList, sortData } = useSort(eldDvirs);

  if (!sortedList) {
    return null;
  }

  return <div>
    <Card>
      <TableContainer sx={{ position: 'relative', overflow: 'scroll' }}>
        <Table>
          <TableHead>
            <TableRow>
              <TruxTableCell />
              <SortableHeader sortData={sortData} sortKey="dvirDate">
                Time
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="vehicleUnitNumber">
                Vehicle
              </SortableHeader>

              <SortableHeader sortData={sortData} sortKey="eldDriverFirstName">
                Driver
              </SortableHeader>

              <TruxTableCell>
                Defects
              </TruxTableCell>

              <TruxTableCell>
                Status
              </TruxTableCell>

              <TruxTableCell align="center">
                Comment
              </TruxTableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {sortedList.map((eldDvir, index) =>
            (<TableRow
              key={index}
              hover
              onClick={() => onEldDvirSelected(eldDvir)}
              sx={{ cursor: "pointer" }}
            >
              <TruxTableCell align="left">
                <IconButton color="error" onClick={(event) => {
                  event.preventDefault();
                  event.stopPropagation();
                  onEldDvirDeleteSelected(eldDvir);
                }}>
                  <Iconify icon="solar:trash-bin-trash-bold" />
                </IconButton>
              </TruxTableCell>
              <TruxTableCell>
                {formatDatetimeForPreview(eldDvir.dvirDate, timezone)}
              </TruxTableCell>

              <TruxTableCell>
                {eldDvir.vehicleUnitNumber}
              </TruxTableCell>

              <TruxTableCell>
                {`${eldDvir.eldDriverFirstName  } ${  eldDvir.eldDriverLastName}`}
              </TruxTableCell>

              <TruxTableCell>
                {eldDvir.vehicleDefects.join(", ")}<br />
                {eldDvir.trailerDefects.join(", ")}
              </TruxTableCell>

              <TruxTableCell>
                {eldDvir.dvirStatus}
              </TruxTableCell>

              <TruxTableCell align="right">
                {eldDvir.dvirComment || ""}
              </TruxTableCell>
            </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    </Card>
  </div >;
});

type PageModel = {
  eldDvirs?: EldDvir[],
  eldDrivers?: EldDriver[]
}

const ITEM_HEIGHT = 48;
const ITEM_PADDING_TOP = 8;
const MenuProps = {
  PaperProps: {
    style: {
      maxHeight: ITEM_HEIGHT * 4.5 + ITEM_PADDING_TOP,
      width: 250,
    },
  },
};

const vehicleDefects = [
  'Air Compressor',
  'Air Lines',
  'Battery',
  'Belts and Hoses',
  'Body',
  'Brake Accessories',
  'Brakes, Parking',
  'Brakes, Service',
  'Clutch',
  'Coupling Devices',
  'Defroster/Heater',
  'Drive Line',
  'Engine',
  'Exhaust',
  'Fifth Wheel',
  'Fluid Levels',
  'Frame and Assembly',
  'Front Axle',
  'Fuel Tanks',
  'Horn(s)',
  'Lights',
  'Brake',
  'Head/Stop',
  'Tail/Dash',
  'Turn Indicators',
  'Clearance/Marker',
  'Mirrors',
  'Muffler',
  'Oil Pressure',
  'Radiator',
  'Rear End',
  'Reflectors',
  'Safety Equipment',
  'Fire Extinguisher',
  'Flags/Flares/Fusees',
  'Reflective Triangles',
  'Spare Fuses',
  'Spare Bulbs (optional)',
  'Starter',
  'Steering',
  'Suspension System',
  'Tire Chains',
  'Tires',
  'Transmission',
  'Trip Recorder',
  'Wheels and Rims',
  'Windows',
  'Windshield Wipers & Washers',
  'Other'
];

const trailerDefects = [
  'Brake Connections',
  'Brakes',
  'Coupling Devices',
  'Coupling (King) Pin',
  'Doors',
  'Hitch',
  'Landing Gear',
  'Lights - All',
  'Reflectors/Reflective Tape',
  'Roof',
  'Suspension System',
  'Tarpaulin',
  'Tires',
  'Wheels and Rims',
  'Other'
];

export default function Page() {
  const settings = useSettingsContext();
  const pageFilterData = usePageFilterLoaderData(
    addDaysToDate(today(), -7),
    today()
  );

  const { session } = useAuthContext();
  const timezone = session?.organization?.companyTimezone as string;

  const dataLoader = useCallback(() => ({
    eldDvirs: !pageFilterData.fromDate ? [] : axios.get(formatEndpoint(endpoints.eldDvirs.findAll, pageFilterData)).then(response => response.data),
    eldDrivers: axios.get(endpoints.eldDrivers.findAll).then(response => response.data),
  }), [pageFilterData]);
  const {
    data,
    updateData,
    setData,
    error,
    loaded,
    loading,
    setShouldRefresh,
  } = useModelData<PageModel>(dataLoader);

  useEffect(() => {
    if (!loading && error) {
      console.log(error);
      alert(error);
    }
  }, [loading, error]);

  const selectedEldDvirModel = useModelSavingData<EldDvir>(
    endpoints.eldDvirs.update,
    endpoints.eldDvirs.create,
    endpoints.eldDvirs.delete
  );
  const [openEldDvirDialog, setOpenEldDvirDialog] = useState(false);
  const [openDeleteEldDvirDialog, setOpenDeleteEldDvirDialog] = useState(false);

  const onEldDvirSelected = useCallback((a: EldDvir) => {
    selectedEldDvirModel.set(a);
    setOpenEldDvirDialog(true);
  }, [selectedEldDvirModel]);

  const onEldDvirDeleteSelected = useCallback((a: EldDvir) => {
    selectedEldDvirModel.set(a);
    setOpenDeleteEldDvirDialog(true);
  }, [selectedEldDvirModel]);

  const deleteDvir = () => {
    selectedEldDvirModel.destroy().then(() => {
      enqueueSnackbar("Successfully deleted the DVIR");
      updateData("eldDvirs")(deleteById(data!.eldDvirs!, selectedEldDvirModel.model!));
      setOpenDeleteEldDvirDialog(false);
    })
  }

  const updateDvir = () => {
    selectedEldDvirModel.save().then(response => {
      if (response) {
        enqueueSnackbar("Successfully saved the DVIR");
        const eldDvir = response.data as EldDvir;

        updateData("eldDvirs")(replaceById(data!.eldDvirs!, eldDvir));

        setOpenEldDvirDialog(false);
      }
    }).catch((err) => {
      if (err.response.status === 422) {
        err.response.data.forEach((msg: any) => {
          enqueueSnackbar(msg.message, {variant: "error"})
        })
      } else {
        enqueueSnackbar("Unexpected error has occurred", {variant: "error"})
      }
    })
  }

  const newDvirButton = <Button
    onClick={() => {
      selectedEldDvirModel.set({
        id: "",
        createdAt: undefined,
        organizationId: session?.organization.id || 0,
        dvirDate: dayjs().tz(timezone).format(),
        dvirStatus: "No Issues",
        eldDriverId: 0,
        eldDriverFirstName: "",
        eldDriverLastName: "",
        vehicleId: 0,
        vehicleUnitNumber: "",
        vehicleVin: "",
        vehicleDefects: [],
        trailers: [],
        trailerDefects: [],
        signatureFileUrl: "",
        dvirComment: "",
      })
      setOpenEldDvirDialog(true);
    }}
    variant="contained"
    color="success"
    startIcon={<Iconify icon="mingcute:add-line" />}
  >
    New DVIR
  </Button>;

  return (
    <Container maxWidth={settings.themeStretch ? false : 'lg'}>
      <DisplayPageData loading={loading} error={error} data={data}>
        <PageHeader title="DVIRs">
          {newDvirButton}
        </PageHeader>

        <PageFilterLoader pageFilterData={pageFilterData} onRun={() => {
          setShouldRefresh(true);
        }} />

        {data && (data.eldDvirs?.length || 0) > 0 && <EldDvirs eldDvirs={data.eldDvirs} onEldDvirSelected={onEldDvirSelected} onEldDvirDeleteSelected={onEldDvirDeleteSelected} timezone={timezone} />}
        {loaded && data && data.eldDvirs?.length === 0 && <NoneFoundReport />}
      </DisplayPageData>

      {openDeleteEldDvirDialog && selectedEldDvirModel.model &&
        <SimpleDialog
          title="Delete DVIR"
          open={openDeleteEldDvirDialog}
          onCancel={() => setOpenDeleteEldDvirDialog(false)}
          onSubmit={deleteDvir}
          submitDisabled={selectedEldDvirModel.loading}
          submitButton="Delete DVIR"
          submitButtonIcon={<Iconify icon="solar:trash-bin-trash-bold" />}
          submitButtonColor="error">

          <DisplayErrors savingData={selectedEldDvirModel} />

          Are you sure you would like to delete this DVIR?
        </SimpleDialog>}

      {openEldDvirDialog && selectedEldDvirModel.model &&
        <SimpleDialog
          size="large"
          transitionDuration={0}
          title={<>DVIR <Label variant="filled" color={selectedEldDvirModel.model.dvirStatus === "Has Issues" ? "error" : "success"}>{selectedEldDvirModel.model.dvirStatus}</Label></>}
          open={openEldDvirDialog}
          onCancel={() => setOpenEldDvirDialog(false)}
          onSubmit={updateDvir}
          submitDisabled={selectedEldDvirModel.loading}
          submitButton={selectedEldDvirModel.isNew ? "New DVIR" : "Update DVIR"}
          submitButtonIcon={selectedEldDvirModel.isNew ? <Iconify icon="mingcute:add-line" /> : <Iconify icon="basil:save-solid" />}
          submitButtonColor={selectedEldDvirModel.isNew ? "success" : "info"}
        >
          <Stack direction="column" gap={1}>
            <TruxFormField label="Driver">
              {selectedEldDvirModel.model.id === "" ?
                <TruxAutocompleteField
                  options={data?.eldDrivers ? [...data.eldDrivers.map(eldDriver => ({
                    label: `${eldDriver.firstName} ${eldDriver.lastName}`,
                    id: eldDriver.id
                  })), {
                    label: "None",
                    id: 0
                  }] : []}
                  onClear={() => {
                    selectedEldDvirModel.update(model => {
                      model.eldDriverId = 0;
                      model.eldDriverFirstName = "";
                      model.eldDriverLastName = "";
                      model.vehicleId = 0;
                      model.vehicleUnitNumber = "";
                      model.vehicleVin = "";
                      model.trailers = [];
                      model.signatureFileUrl = "";
                      model.dvirComment = "";
                    })
                  }}
                  onChange={(value) => {
                    selectedEldDvirModel.update(model => {
                      const eldDriverId = value === null ? model.eldDriverId : Number(value?.id);
                      model.eldDriverId = eldDriverId;

                      data?.eldDrivers?.forEach(eldDriver => {
                        if (eldDriverId === eldDriver.id) {
                          model.eldDriverId = eldDriver.id;
                          model.eldDriverFirstName = eldDriver.firstName;
                          model.eldDriverLastName = eldDriver.lastName;
                          model.vehicleId = eldDriver.eldVehicleId;
                          model.vehicleUnitNumber = eldDriver.eldVehicleUnitNumber;
                          model.vehicleVin = eldDriver.eldVehicleVin;
                          model.trailers = eldDriver.trailers;
                          model.signatureFileUrl = eldDriver.signatureUrl;
                        }
                      })
                    })
                  }}
                  selected={{
                    id: selectedEldDvirModel.model.eldDriverId,
                    label: selectedEldDvirModel.model.eldDriverId === 0 ? "None" : `${selectedEldDvirModel.model.eldDriverFirstName} ${selectedEldDvirModel.model.eldDriverLastName}`,
                  }}
                />
                : `${selectedEldDvirModel.model.eldDriverFirstName} ${selectedEldDvirModel.model.eldDriverLastName}`}
            </TruxFormField>

            <TruxFormField label="Time" >
              <TruxDateTimeField limitWidth={false} modelSavingData={selectedEldDvirModel} field="dvirDate" timezone={timezone} />
            </TruxFormField>
            <TruxFormField label="Vehicle">
              <b>#{selectedEldDvirModel.model.vehicleUnitNumber}</b>
            </TruxFormField>

            <TruxFormField label="Vehicle Defects">
              <Select
                sx={{width: "100%"}}
                multiple
                value={selectedEldDvirModel.model.vehicleDefects}
                onChange={({target}) => {
                  selectedEldDvirModel.update(dvir => {
                    if (target.value instanceof Array) {
                      dvir.vehicleDefects = target.value;
                    } else {
                      dvir.vehicleDefects = [target.value];
                    }
                  });
                }}
                input={<OutlinedInput />}
                MenuProps={MenuProps}
              >
                {vehicleDefects.map((defect) => (
                  <MenuItem
                    key={defect}
                    value={defect}
                    sx={{background: selectedEldDvirModel?.model?.vehicleDefects.includes(defect) ? `${Colors.lightGreen} !important` : undefined}}
                  >
                    {defect}
                  </MenuItem>
                ))}
              </Select>
            </TruxFormField>

            <TruxFormField label="Trailers">
              <b>{selectedEldDvirModel.model.trailers.filter(t => t !== "").length ?
                `${selectedEldDvirModel.model.trailers.filter(t => t !== "").join(", ")}` :
                "No Trailers"
              }</b>
            </TruxFormField>

            <TruxFormField label="Vehicle Defects">
              <Select
                sx={{width: "100%"}}
                multiple
                value={selectedEldDvirModel.model.trailerDefects}
                onChange={({target}) => {
                  selectedEldDvirModel.update(dvir => {
                    if (target.value instanceof Array) {
                      dvir.trailerDefects = target.value;
                    } else {
                      dvir.trailerDefects = [target.value];
                    }
                  });
                }}
                input={<OutlinedInput />}
                MenuProps={MenuProps}
              >
                {trailerDefects.map((defect) => (
                  <MenuItem
                    key={defect}
                    value={defect}
                    sx={{background: selectedEldDvirModel?.model?.trailerDefects.includes(defect) ? `${Colors.lightGreen} !important` : undefined}}
                  >
                    {defect}
                  </MenuItem>
                ))}
              </Select>
            </TruxFormField>
            <TruxFormField label="Comment">
              <TruxTextField modelSavingData={selectedEldDvirModel} field="dvirComment" multiline />
            </TruxFormField>

            {selectedEldDvirModel.model.signatureFileUrl && <TruxFormField label="Signature">
              <img style={{ maxWidth: "200px" }} src={selectedEldDvirModel.model.signatureFileUrl} alt="driver signature" />
            </TruxFormField>}
          </Stack>
        </SimpleDialog>}
    </Container >
  );
}